import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import MuiButton from "@mui/material/Button";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useAvailableLanguagesSetting } from "@/hooks/useSetting";
import { useCategoryByType } from "@/hooks/useCategories";
import {
  ExternalProvider,
  ExternalProviderLanguage,
  ExternalProviderPayload,
} from "@/domain/externalProviders.interface";
import externalProviderValidationSchema, {
  ExternalProviderFormData,
} from "./validation.schema";
import ProviderLanguageFields from "./ProviderLanguageFields";

const EXTERNAL_PROVIDER_CATEGORY_TYPE = "EXTERNAL_PROVIDERS";

type ExternalProviderModalProps = {
  open: boolean;
  onClose: () => void;
  externalProvider?: ExternalProvider;
  onSave: (data: ExternalProviderPayload) => Promise<void>;
  isPending: boolean;
};

const EMPTY_LANGUAGE = {
  language: "",
  description: "",
  services: "",
  restrictions: "",
  hours: [],
};

const DEFAULT_VALUES: ExternalProviderFormData = {
  categoryId: "",
  name: "",
  description: "",
  email: "",
  website: "",
  phoneNumber: "",
  allowPP: "",
  status: "ACTIVE",
  locationName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  locationRestrictions: "",
  serviceName: "",
  serviceDescription: "",
  rewardsType: "",
  parcaPoints: "",
  languages: [EMPTY_LANGUAGE],
};

const splitTags = (value?: string): string[] =>
  (value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const joinTags = (tags?: string[]): string => (tags || []).join(", ");

const pickCategoryLabel = (
  langs: { name: string; language: string }[] | undefined
) => {
  if (!langs || langs.length === 0) return "";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).name;
};

const ExternalProviderModal: FC<ExternalProviderModalProps> = ({
  open,
  onClose,
  externalProvider,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!externalProvider;

  const availableLanguages = useAvailableLanguagesSetting();
  const { category, isLoadingCategory } = useCategoryByType({
    type: EXTERNAL_PROVIDER_CATEGORY_TYPE,
    status: "ACTIVE",
  });

  const languageOptions = useMemo(
    () => availableLanguages.map((code) => ({ key: code, value: code })),
    [availableLanguages]
  );

  const categoryOptions = useMemo(
    () =>
      (category?.categories || []).map((c) => ({
        key: c._id,
        value: pickCategoryLabel(c.languages) || c._id,
      })),
    [category]
  );

  const methods = useForm<ExternalProviderFormData>({
    resolver: yupResolver(externalProviderValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  useEffect(() => {
    if (externalProvider) {
      reset({
        categoryId: externalProvider.categoryId,
        name: externalProvider.name,
        description: externalProvider.description,
        email: externalProvider.email || "",
        website: externalProvider.website || "",
        phoneNumber: externalProvider.phoneNumber || "",
        allowPP: externalProvider.allowPP || "",
        status: (externalProvider.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
        locationName: externalProvider.locations?.name || "",
        addressLine1: externalProvider.locations?.addressLine1 || "",
        addressLine2: externalProvider.locations?.addressLine2 || "",
        city: externalProvider.locations?.city || "",
        state: externalProvider.locations?.state || "",
        zip: externalProvider.locations?.zip || "",
        locationRestrictions: joinTags(
          externalProvider.locations?.restrictions
        ),
        serviceName: externalProvider.services?.name || "",
        serviceDescription: externalProvider.services?.description || "",
        rewardsType: externalProvider.services?.rewardsType || "",
        parcaPoints: externalProvider.services?.parcaPoints || "",
        languages:
          externalProvider.languages && externalProvider.languages.length > 0
            ? externalProvider.languages.map((l) => ({
                language: l.language,
                description: l.description || "",
                services: joinTags(l.services),
                restrictions: joinTags(l.restrictions),
                hours: (l.hours || []).map((h) => ({
                  day: h.day,
                  hour: h.hour,
                })),
              }))
            : [EMPTY_LANGUAGE],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [externalProvider, reset]);

  const onSubmit = async (data: ExternalProviderFormData) => {
    const currentUserId = userInfo?._id as string;

    const languages: ExternalProviderLanguage[] = (data.languages || []).map(
      (l) => ({
        language: l.language,
        ...(l.description ? { description: l.description.trim() } : {}),
        services: splitTags(l.services),
        restrictions: splitTags(l.restrictions),
        hours: (l.hours || []).map((h) => ({ day: h.day, hour: h.hour })),
      })
    );

    const hasLocation =
      !!data.locationName ||
      !!data.addressLine1 ||
      !!data.addressLine2 ||
      !!data.city ||
      !!data.state ||
      !!data.zip ||
      !!data.locationRestrictions;

    const hasService =
      !!data.serviceName ||
      !!data.serviceDescription ||
      !!data.rewardsType ||
      !!data.parcaPoints;

    const payload: ExternalProviderPayload = {
      categoryId: data.categoryId,
      name: data.name.trim(),
      description: data.description.trim(),
      languages,
      ...(data.email ? { email: data.email.trim() } : {}),
      ...(data.website ? { website: data.website.trim() } : {}),
      ...(data.phoneNumber ? { phoneNumber: data.phoneNumber.trim() } : {}),
      ...(data.allowPP ? { allowPP: data.allowPP.trim() } : {}),
      ...(data.status ? { status: data.status } : {}),
      ...(hasLocation
        ? {
            locations: {
              name: data.locationName.trim(),
              addressLine1: data.addressLine1.trim(),
              addressLine2: data.addressLine2.trim(),
              city: data.city.trim(),
              state: data.state.trim(),
              zip: data.zip.trim(),
              restrictions: splitTags(data.locationRestrictions),
            },
          }
        : {}),
      ...(hasService
        ? {
            services: {
              name: data.serviceName.trim(),
              description: data.serviceDescription.trim(),
              rewardsType: data.rewardsType.trim(),
              parcaPoints: data.parcaPoints.trim(),
            },
          }
        : {}),
      ...(isEdit
        ? { updatedBy: currentUserId }
        : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit External Provider" : "New external provider"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 640,
        maxHeight: 800,
        maxWidth: 960,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={categoryOptions}
                      value={field.value as string}
                      label="Category"
                      id="provider-category"
                      required
                      isLoading={isLoadingCategory}
                      error={!!errors.categoryId}
                      errorMessage={errors.categoryId?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="provider-status"
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-email"
                      label="Email"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.email}
                      helperText={errors.email ? errors.email.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-description"
                      label="Description"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.description}
                      helperText={
                        errors.description ? errors.description.message : null
                      }
                      multiline
                      minRows={2}
                      maxRows={5}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-website"
                      label="Website"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-phone"
                      label="Phone number"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="allowPP"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-allowpp"
                      label="Allow PP"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Location (single object) */}
              <Grid item xs={12}>
                <Typography variant="titleSmall">Location</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="locationName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-location-name"
                      label="Location name"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="addressLine1"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-address1"
                      label="Address line 1"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="addressLine2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-address2"
                      label="Address line 2"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-city"
                      label="City"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-state"
                      label="State"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Controller
                  name="zip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-zip"
                      label="Zip"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="locationRestrictions"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-location-restrictions"
                      label="Location restrictions (comma separated)"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Service (single object) */}
              <Grid item xs={12}>
                <Typography variant="titleSmall">Service</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="serviceName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-service-name"
                      label="Service name"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="serviceDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-service-description"
                      label="Service description"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="rewardsType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-rewards-type"
                      label="Rewards type"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="parcaPoints"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="provider-parca-points"
                      label="Parca points"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Languages */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="titleSmall">Languages *</Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => append(EMPTY_LANGUAGE)}
                  >
                    Add language
                  </MuiButton>
                </Box>
                {typeof errors.languages?.message === "string" && (
                  <Typography
                    variant="bodySmall"
                    sx={{ color: theme.palette.error.main, mb: 1 }}
                  >
                    {errors.languages.message}
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {fields.map((langField, index) => (
                    <ProviderLanguageFields
                      key={langField.id}
                      langIndex={index}
                      onRemove={() => remove(index)}
                      canRemove={fields.length > 1}
                      languageOptions={languageOptions}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default ExternalProviderModal;
